import React, { useMemo, useRef, useState, useEffect, useCallback } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { Html, OrbitControls, Stars } from '@react-three/drei';

// Types from talent-skill-service
interface UserSkill {
  id: string;
  name: string;
  description?: string;
  category?: string;
  proficiency: number;
  endorsements?: number;
  yearsOfExperience?: number;
  lastUsed?: string;
  evidence?: Array<{
    type: 'certification' | 'project' | 'work' | 'education';
    title: string;
    issuer?: string;
    date?: string;
    url?: string;
  }>;
}

interface NodeSkill {
  name: string;
  pos: [number, number, number];
  level: number; // maps to size/intensity
  color: string;
  category?: string;
  description?: string;
  endorsements?: number;
}

const CATEGORY_COLORS: Record<string, string> = {
  professional: '#D4AF37', // Gold
  practical: '#4A90E2',    // Blue
  physical: '#7ED321',     // Green
  soft: '#9013FE',         // Purple
  general: '#D4AF37'
};

function colorForCategory(category?: string): string {
  if (!category) return '#D4AF37';
  const key = category.toLowerCase();
  return CATEGORY_COLORS[key] || '#D4AF37';
}

function computePositions(skills: UserSkill[]): [number, number, number][] {
  const n = skills.length;
  const positions: [number, number, number][] = [];

  // Group by category for clustering
  const categoriesMap = new Map<string, number[]>();
  skills.forEach((s, i) => {
    const cat = s.category?.toLowerCase() || 'general';
    if (!categoriesMap.has(cat)) categoriesMap.set(cat, []);
    categoriesMap.get(cat)!.push(i);
  });

  const categoryEntries = Array.from(categoriesMap.entries());
  const categoryCount = categoryEntries.length;

  categoryEntries.forEach(([cat, indices], catIdx) => {
    const angleOffset = (catIdx / categoryCount) * Math.PI * 2;
    const clusterCenterScale = 1.5 + Math.min(1.0, n * 0.05);
    const clusterX = Math.cos(angleOffset) * clusterCenterScale;
    const clusterZ = Math.sin(angleOffset) * clusterCenterScale;
    const clusterY = Math.sin(catIdx) * 0.5;

    indices.forEach((skillIdx, i) => {
      const localAngle = (i / indices.length) * Math.PI * 2;
      const localRadius = 0.4 + (indices.length * 0.05);
      const x = clusterX + Math.cos(localAngle) * localRadius;
      const z = clusterZ + Math.sin(localAngle) * localRadius;
      const y = clusterY + Math.sin(localAngle * 2) * 0.3;

      positions[skillIdx] = [x, y, z];
    });
  });

  return positions;
}

function SkillNode({ skill, onHover, onClick }: { skill: NodeSkill; onHover: (s: NodeSkill | null) => void; onClick: (s: NodeSkill) => void }) {
  const mesh = useRef<any>();
  const [hovered, setHovered] = useState(false);

  useFrame(({ clock }) => {
    if (mesh.current) {
      const baseScale = hovered ? 1.2 : 1;
      mesh.current.scale.setScalar(baseScale + Math.sin(clock.getElapsedTime() * 2 + skill.level) * 0.05);
      mesh.current.rotation.y = clock.getElapsedTime() * 0.5;
    }
  });

  return (
    <group>
      <mesh
        ref={mesh}
        position={skill.pos}
        onPointerOver={() => { onHover(skill); setHovered(true); }}
        onPointerOut={() => { onHover(null); setHovered(false); }}
        onClick={() => onClick(skill)}
      >
        <icosahedronGeometry args={[0.15 + (skill.level || 0) * 0.08, 2]} />
        <meshStandardMaterial
          color={skill.color}
          emissive={skill.color}
          emissiveIntensity={hovered ? 0.4 : 0.2 + (skill.level || 0) * 0.05}
          metalness={0.8}
          roughness={0.2}
        />
      </mesh>

      {/* Skill level indicator rings */}
      {Array.from({ length: skill.level }, (_, i) => (
        <mesh key={i} position={skill.pos} rotation={[Math.PI / 2, 0, 0]}>
          <ringGeometry args={[0.25 + i * 0.1, 0.27 + i * 0.1, 16]} />
          <meshBasicMaterial color={skill.color} transparent opacity={0.3} />
        </mesh>
      ))}

      <Html position={[skill.pos[0], skill.pos[1] + 0.4 + (skill.level || 0) * 0.05, skill.pos[2]]} center>
        <div className={`text-xs px-2 py-1 rounded shadow transition-all duration-200 font-serif border ${hovered ? 'bg-premium-gold text-premium-wood-dark border-premium-gold scale-110 font-bold' : 'bg-black/60 text-premium-gold border-premium-gold/30'
          }`}>
          {skill.name}
        </div>
      </Html>
    </group>
  );
}

function ConnectionLine({ from, to, color }: { from: [number, number, number]; to: [number, number, number]; color: string }) {
  const lineRef = useRef<any>();

  useFrame(({ clock }) => {
    if (lineRef.current) {
      const pulse = Math.sin(clock.getElapsedTime() * 2) * 0.15;
      lineRef.current.material.opacity = 0.2 + pulse;
    }
  });

  return (
    <line ref={lineRef}>
      <bufferGeometry attach="geometry">
        <bufferAttribute
          attach="attributes-position"
          count={2}
          array={new Float32Array([...from, ...to])}
          itemSize={3}
        />
      </bufferGeometry>
      <lineBasicMaterial color={color} linewidth={1} transparent opacity={0.2} />
    </line>
  );
}

interface SkillGridProps {
  userSkills: UserSkill[];
  loading: boolean;
  onRefresh?: () => void;
}

const SkillGrid: React.FC<SkillGridProps> = ({
  userSkills: propUserSkills,
  loading: propLoading,
  onRefresh
}) => {
  const [hoveredSkill, setHoveredSkill] = useState<NodeSkill | null>(null);
  const [selectedSkill, setSelectedSkill] = useState<NodeSkill | null>(null);
  const [error, setError] = useState('');
  const [showStats, setShowStats] = useState(true);
  const [focusedSkillIndex, setFocusedSkillIndex] = useState<number>(-1);
  const [announcement, setAnnouncement] = useState('');
  const skillListRef = useRef<HTMLDivElement>(null);

  // Use props instead of fetching
  const apiSkills = propUserSkills;

  const nodeSkills: NodeSkill[] = useMemo(() => {
    const list = apiSkills || [];
    if (list.length === 0) return [];

    const positions = computePositions(list);
    return list.map((s, i) => ({
      name: s.name,
      pos: positions[i] || [0, 0, 0],
      level: Math.max(1, Math.min(5, s.proficiency || 2)),
      color: colorForCategory(s.category),
      category: s.category,
      description: s.description,
      endorsements: s.endorsements || 0
    }));
  }, [apiSkills]);

  const connections: [number, [number, number, number], string][] = useMemo(() => {
    return nodeSkills.map((s, i) => [i, [0, 0, 0], s.color]);
  }, [nodeSkills]);

  const categories = useMemo(() => {
    const set = new Map<string, string>();
    nodeSkills.forEach(s => set.set(s.category || 'General', s.color));
    return Array.from(set.entries());
  }, [nodeSkills]);

  // Keyboard navigation for skills
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (nodeSkills.length === 0) return;

    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        e.preventDefault();
        setFocusedSkillIndex(prev => {
          const next = (prev + 1) % nodeSkills.length;
          setSelectedSkill(nodeSkills[next]);
          setAnnouncement(`Selected ${nodeSkills[next].name}, Level ${nodeSkills[next].level}, ${nodeSkills[next].category || 'General'}`);
          return next;
        });
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        e.preventDefault();
        setFocusedSkillIndex(prev => {
          const next = prev <= 0 ? nodeSkills.length - 1 : prev - 1;
          setSelectedSkill(nodeSkills[next]);
          setAnnouncement(`Selected ${nodeSkills[next].name}, Level ${nodeSkills[next].level}, ${nodeSkills[next].category || 'General'}`);
          return next;
        });
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (focusedSkillIndex >= 0) {
          setSelectedSkill(nodeSkills[focusedSkillIndex]);
          setAnnouncement(`Viewing details for ${nodeSkills[focusedSkillIndex].name}`);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setSelectedSkill(null);
        setFocusedSkillIndex(-1);
        setAnnouncement('Skill details closed');
        break;
    }
  }, [nodeSkills, focusedSkillIndex]);

  // Initialize focused skill
  useEffect(() => {
    if (nodeSkills.length > 0 && focusedSkillIndex === -1) {
      setFocusedSkillIndex(0);
    }
  }, [nodeSkills, focusedSkillIndex]);

  // --- Browser History / Back Button Intercept ---
  useEffect(() => {
    const handlePopState = (e: PopStateEvent) => {
      if (selectedSkill !== null) {
        setSelectedSkill(null);
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [selectedSkill]);

  // Announce changes for screen readers
  useEffect(() => {
    if (nodeSkills.length > 0) {
      setAnnouncement(`Skill Universe loaded with ${nodeSkills.length} skills. Use arrow keys to navigate, Enter to select, Escape to close details.`);
    }
  }, [nodeSkills.length]);

  return (
    <div>
      {/* Screen Reader Announcements */}
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
      >
        {announcement}
      </div>

      <div className="flex items-baseline justify-between mb-6">
        <div>
          <h2 className="text-2xl font-serif font-bold text-premium-gold">3D Skill Universe</h2>
          <p className="text-white/70 text-sm mt-1">Interactive visualization of your professional capabilities</p>
        </div>
        <div className="text-right">
          <div className="text-xs text-white/50 mt-1">🖱️ Drag to rotate • 🔍 Scroll to zoom • 👆 Click nodes • ⌨️ Arrow keys to navigate</div>
        </div>
      </div>
      <div
        className="w-full h-96 bg-black/40 rounded-xl border border-white/10 relative overflow-hidden"
        role="application"
        aria-label="Interactive 3D Skill Universe Visualization"
        tabIndex={0}
        onKeyDown={handleKeyDown}
        ref={skillListRef}
      >
        <Canvas camera={{ position: [0, 0, 4], fov: 60 }}>
          <Stars radius={50} depth={30} count={500} factor={2} saturation={0} fade speed={1} />
          <ambientLight intensity={0.5} />
          <directionalLight position={[10, 10, 5]} intensity={0.8} />
          <pointLight position={[0, 0, 0]} intensity={0.5} color="#D4AF37" />
          {/* Connections: All lines lead to the Core */}
          {connections.map(([nodeIdx, centerPos, color], i) => (
            <ConnectionLine
              key={i}
              from={nodeSkills[nodeIdx].pos}
              to={centerPos as [number, number, number]}
              color={color}
            />
          ))}
          {/* Skill nodes */}
          {nodeSkills.map((s, i) => (
            <SkillNode
              key={i}
              skill={s}
              onHover={setHoveredSkill}
              onClick={(skill) => {
                setSelectedSkill(skill);
                window.history.pushState({ skillLevel: 'details' }, '', window.location.hash);
              }}
            />
          ))}

          {/* Center core: The Middleman Soul */}
          <group>
            <mesh position={[0, 0, 0]}>
              <sphereGeometry args={[0.25, 32, 32]} />
              <meshStandardMaterial
                color="#D4AF37"
                emissive="#D4AF37"
                emissiveIntensity={1.5}
                metalness={1}
                roughness={0}
              />
            </mesh>
            <mesh position={[0, 0, 0]}>
              <sphereGeometry args={[0.4, 32, 32]} />
              <meshStandardMaterial color="#D4AF37" transparent opacity={0.15} />
            </mesh>
            <Html position={[0, -0.5, 0]} center>
              <div className="text-[10px] font-bold text-premium-gold bg-black/60 px-3 py-1 rounded-full border border-premium-gold/30 uppercase tracking-[0.3em] backdrop-blur-md">
                Core Talent
              </div>
            </Html>

            {/* Ambient inner glow */}
            <pointLight position={[0, 0, 0]} intensity={2} color="#D4AF37" distance={2} />
          </group>
          <OrbitControls enablePan enableZoom enableRotate />
        </Canvas>
        {showStats && (
          <div className="absolute right-2 top-2 bg-black/80 backdrop-blur-sm rounded-lg p-3 text-xs text-premium-gold shadow-lg pointer-events-auto max-w-[300px] border border-premium-gold/20">
            <div className="flex justify-between items-center mb-2">
              <div className="font-bold text-white font-serif">Skill Categories</div>
              <button onClick={() => setShowStats(false)} className="text-white/50 hover:text-white">✕</button>
            </div>
            {categories.map(([cat, col]) => {
              const count = nodeSkills.filter(s => (s.category || 'General') === cat).length;
              return (
                <div key={cat} className="flex items-center justify-between mb-1">
                  <div className="flex items-center">
                    <span className="inline-block w-3 h-3 rounded-full mr-2" style={{ background: col }}></span>
                    <span>{cat}</span>
                  </div>
                  <span className="text-white/70">{count}</span>
                </div>
              );
            })}
            <div className="mt-2 pt-2 border-t border-white/10 text-white/70">
              <div>• Node size = proficiency level</div>
              <div>• Rings = skill mastery</div>
              <div>• Click nodes for details</div>
            </div>
          </div>
        )}

        {!showStats && (
          <button
            onClick={() => setShowStats(true)}
            className="absolute right-2 top-2 bg-black/60 backdrop-blur-sm rounded-lg p-2 text-premium-gold hover:text-white transition-colors"
          >
            📊
          </button>
        )}
        {hoveredSkill && !selectedSkill && (
          <div className="absolute left-1/2 -translate-x-1/2 bottom-3 bg-gradient-to-r from-premium-gold-dark to-premium-gold-light text-premium-wood-dark px-4 py-2 rounded-lg shadow-lg font-semibold animate-fade-in pointer-events-auto">
            <div className="text-center">
              <div className="font-bold font-serif">{hoveredSkill.name}</div>
              <div className="text-xs opacity-90">
                Level {hoveredSkill.level} • {hoveredSkill.category || 'General'}
                {hoveredSkill.endorsements ? ` • ${hoveredSkill.endorsements} endorsements` : ''}
              </div>
            </div>
          </div>
        )}

        {selectedSkill && (
          <div className="absolute left-4 bottom-4 bg-black/90 backdrop-blur-sm rounded-lg p-4 text-white shadow-xl pointer-events-auto max-w-[300px] border border-premium-gold/30">
            <div className="flex justify-between items-start mb-2">
              <h3 className="font-bold text-premium-gold font-serif text-lg">{selectedSkill.name}</h3>
              <button
                onClick={() => {
                  setSelectedSkill(null);
                  if (window.history.state?.skillLevel) window.history.back();
                }}
                className="text-white/50 hover:text-white ml-2"
              >✕</button>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-white/70">Proficiency:</span>
                <div className="flex items-center">
                  {Array.from({ length: 5 }, (_, i) => (
                    <span key={i} className={`text-lg ${i < selectedSkill.level ? 'text-premium-gold' : 'text-white/20'}`}>★</span>
                  ))}
                  <span className="ml-2 text-premium-gold">{selectedSkill.level}/5</span>
                </div>
              </div>
              <div className="flex justify-between">
                <span className="text-white/70">Category:</span>
                <span className="text-white">{selectedSkill.category || 'General'}</span>
              </div>
              {selectedSkill.endorsements !== undefined && (
                <div className="flex justify-between">
                  <span className="text-white/70">Endorsements:</span>
                  <span className="text-premium-gold">{selectedSkill.endorsements}</span>
                </div>
              )}
              {selectedSkill.description && (
                <div className="mt-3 pt-2 border-t border-white/10">
                  <p className="text-white/80 text-xs leading-relaxed">{selectedSkill.description}</p>
                </div>
              )}
            </div>
          </div>
        )}
        <div className="absolute left-2 bottom-2 text-xs text-white/40 opacity-80">
          <span role="status">Accessible: Use mouse to drag/zoom, or keyboard arrows to navigate skills. Press Enter to select, Escape to close. Screen reader enabled.</span>
        </div>
      </div>
      {propLoading && <div className="text-xs text-white/70 mt-2">Loading skills...</div>}
      {error && <div className="text-xs text-red-400 mt-2">{error}</div>}
      {!propLoading && apiSkills && apiSkills.length === 0 && (
        <div className="text-center py-8 text-white/70">
          <div className="text-4xl mb-2">🎯</div>
          <div className="text-lg mb-1">No skills found</div>
          <div className="text-sm">Add some skills to see your 3D visualization!</div>
        </div>
      )}

      {nodeSkills.length > 0 && (
        <>
          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
            <div className="bg-white/5 border border-white/10 rounded p-3 text-center" role="region" aria-label="Total Skills Statistic">
              <div className="text-premium-gold font-bold text-lg" aria-label={`${nodeSkills.length} total skills`}>{nodeSkills.length}</div>
              <div className="text-white/70">Total Skills</div>
            </div>
            <div className="bg-white/5 border border-white/10 rounded p-3 text-center" role="region" aria-label="Average Skill Level">
              <div className="text-premium-gold font-bold text-lg" aria-label={`${Math.round(nodeSkills.reduce((sum, s) => sum + s.level, 0) / nodeSkills.length * 10) / 10} average level`}>{Math.round(nodeSkills.reduce((sum, s) => sum + s.level, 0) / nodeSkills.length * 10) / 10}</div>
              <div className="text-white/70">Avg Level</div>
            </div>
            <div className="bg-white/5 border border-white/10 rounded p-3 text-center" role="region" aria-label="Skill Categories Count">
              <div className="text-premium-gold font-bold text-lg" aria-label={`${categories.length} categories`}>{categories.length}</div>
              <div className="text-white/70">Categories</div>
            </div>
            <div className="bg-white/5 border border-white/10 rounded p-3 text-center" role="region" aria-label="Total Endorsements">
              <div className="text-premium-gold font-bold text-lg" aria-label={`${nodeSkills.reduce((sum, s) => sum + (s.endorsements || 0), 0)} endorsements`}>{nodeSkills.reduce((sum, s) => sum + (s.endorsements || 0), 0)}</div>
              <div className="text-white/70">Endorsements</div>
            </div>
          </div>

          {/* Accessible Skill List for Screen Readers */}
          <details className="mt-4 bg-white/5 border border-white/10 rounded p-4">
            <summary className="text-white cursor-pointer font-medium hover:text-premium-gold transition-colors">
              📋 Text-based Skill List (Screen Reader Friendly)
            </summary>
            <ul className="mt-3 space-y-2 max-h-64 overflow-y-auto" role="list" aria-label="Complete list of skills">
              {nodeSkills.map((skill, index) => (
                <li
                  key={index}
                  className="text-sm text-white/80 hover:text-premium-gold cursor-pointer p-2 rounded hover:bg-white/5 transition-colors"
                  onClick={() => {
                    setSelectedSkill(skill);
                    setFocusedSkillIndex(index);
                    setAnnouncement(`Selected ${skill.name}`);
                    window.history.pushState({ skillLevel: 'details' }, '', window.location.hash);
                  }}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setSelectedSkill(skill);
                      setFocusedSkillIndex(index);
                      setAnnouncement(`Selected ${skill.name}`);
                      window.history.pushState({ skillLevel: 'details' }, '', window.location.hash);
                    }
                  }}
                  aria-label={`${skill.name}, Level ${skill.level} out of 5, ${skill.category || 'General'} category${skill.endorsements ? `, ${skill.endorsements} endorsements` : ''}`}
                >
                  <span className="font-medium" style={{ color: skill.color }}>{skill.name}</span>
                  <span className="text-white/60 ml-2">— Level {skill.level}/5</span>
                  <span className="text-white/50 ml-2 text-xs">{skill.category || 'General'}</span>
                  {skill.endorsements ? <span className="text-premium-gold ml-2 text-xs">★ {skill.endorsements}</span> : ''}
                </li>
              ))}
            </ul>
          </details>
        </>
      )}
    </div>
  );
};

export default SkillGrid;
